import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  endorsementCatalog,
  type EndorsementFlowMode,
  type EndorsementReason,
  type EndorsementSegregationMode,
  type EndorsementSubtype,
  type EndorsementType,
} from '@/shared/api/resources';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { useToast } from '@/shared/ui/useToast';

/**
 * Configuracao do endosso da empresa (P-END-1), para quem tem
 * `endossos.configuracao.update`.
 *
 * O fluxo do endosso enviado (a operacao do tipo endosso pela esteira, padrao do
 * OnPoint, ou a solicitacao com decisao em Endossos), a segregacao entre quem pede e
 * quem decide, a habilitacao dos quatro tipos e o catalogo de subtipos, motivos e
 * documentos exigidos. Nenhuma lista vem carregada: as listas de referencia conflitam
 * (END-001), e a seguradora cadastra a dela.
 */
export function EndorsementSettingsPanel({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const configuracao = useQuery({
    queryKey: ['endorsement-settings'],
    queryFn: ({ signal }) => endorsementCatalog.settings(signal),
    enabled: open,
  });
  const tipos = useQuery({
    queryKey: ['endorsement-types', 'todos'],
    queryFn: ({ signal }) => endorsementCatalog.types(false, signal),
    enabled: open,
  });

  const [fluxo, setFluxo] = useState<EndorsementFlowMode | ''>('');
  const [segregacao, setSegregacao] = useState<EndorsementSegregationMode | ''>('');
  const fluxoAtual = fluxo || configuracao.data?.flow_mode || 'operacao';
  const segregacaoAtual =
    segregacao || configuracao.data?.segregation_mode || 'registrar';

  const gravarConfiguracao = useMutation({
    mutationFn: () =>
      endorsementCatalog.updateSettings({
        flow_mode: fluxoAtual,
        segregation_mode: segregacaoAtual,
      }),
    onSuccess: () => {
      toast.success('Configuracao do endosso gravada.');
      void queryClient.invalidateQueries({ queryKey: ['endorsement-settings'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Configuracao do endosso"
      description="Fluxo, segregacao, tipos habilitados e o catalogo de subtipos, motivos e documentos."
      size="lg"
      footer={<Button onClick={onClose}>Fechar</Button>}
    >
      <div className="flex flex-col gap-6">
        <section className="flex flex-col gap-3">
          <h3 className="text-sm font-semibold text-ink-900">Fluxo do endosso</h3>
          {configuracao.isError ? (
            <Alert tone="danger" title="Nao foi possivel ler a configuracao">
              {configuracao.error.message}
            </Alert>
          ) : null}
          <div className="grid gap-3 sm:grid-cols-2">
            <Select
              label="Fluxo do endosso"
              value={fluxoAtual}
              options={[
                { value: 'operacao', label: 'Operacao do tipo endosso pela esteira' },
                { value: 'solicitacao', label: 'Solicitacao com decisao em Endossos' },
              ]}
              onChange={(event) => setFluxo(event.target.value as EndorsementFlowMode)}
            />
            <Select
              label="Segregacao"
              value={segregacaoAtual}
              options={[
                { value: 'registrar', label: 'Registrar quando quem pediu decide' },
                { value: 'recusar', label: 'Recusar a decisao de quem pediu' },
              ]}
              onChange={(event) =>
                setSegregacao(event.target.value as EndorsementSegregationMode)
              }
            />
          </div>
          <div>
            <Button
              variant="secondary"
              loading={gravarConfiguracao.isPending}
              onClick={() => gravarConfiguracao.mutate()}
            >
              Salvar fluxo
            </Button>
          </div>
        </section>

        <section className="flex flex-col gap-3">
          <h3 className="text-sm font-semibold text-ink-900">Tipos de endosso</h3>
          {tipos.isError ? (
            <Alert tone="danger" title="Nao foi possivel ler os tipos">
              {tipos.error.message}
            </Alert>
          ) : null}
          {(tipos.data ?? []).map((tipo) => (
            <TipoDeEndosso key={tipo.id} tipo={tipo} />
          ))}
        </section>
      </div>
    </Modal>
  );
}

function TipoDeEndosso({ tipo }: { tipo: EndorsementType }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [novo, setNovo] = useState('');

  const subtipos = useQuery({
    queryKey: ['endorsement-subtypes', 'todos', tipo.id],
    queryFn: ({ signal }) => endorsementCatalog.subtypes(tipo.id, true, signal),
    enabled: aberto,
  });
  const invalidar = () => {
    void queryClient.invalidateQueries({ queryKey: ['endorsement-types'] });
    void queryClient.invalidateQueries({ queryKey: ['endorsement-subtypes'] });
  };

  const habilitar = useMutation({
    mutationFn: (enabled: boolean) =>
      endorsementCatalog.updateType(tipo.id, { enabled }),
    onSuccess: invalidar,
    onError: (error: Error) => toast.error(error.message),
  });
  const criar = useMutation({
    mutationFn: () =>
      endorsementCatalog.createSubtype({ type_id: tipo.id, name: novo }),
    onSuccess: () => {
      setNovo('');
      invalidar();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="rounded-md border border-line">
      <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-ink-900">{tipo.name}</p>
          <p className="text-2xs text-ink-500">
            {tipo.description || 'Sem descricao'}
            {tipo.calculation_pending
              ? ' - calculo pendente: o envio fica bloqueado'
              : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone={tipo.enabled ? 'success' : 'neutral'}>
            {tipo.enabled ? 'Habilitado' : 'Desligado'}
          </Badge>
          <Button
            size="sm"
            variant="secondary"
            loading={habilitar.isPending}
            onClick={() => habilitar.mutate(!tipo.enabled)}
          >
            {tipo.enabled ? `Desligar ${tipo.name}` : `Habilitar ${tipo.name}`}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setAberto((valor) => !valor)}
          >
            {aberto ? 'Fechar subtipos' : `Subtipos (${tipo.active_subtypes})`}
          </Button>
        </div>
      </div>
      {aberto ? (
        <div className="flex flex-col gap-2 border-t border-line px-3 py-2">
          {(subtipos.data ?? []).map((subtipo) => (
            <Subtipo key={subtipo.id} subtipo={subtipo} onMudou={invalidar} />
          ))}
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (novo.trim() !== '') criar.mutate();
            }}
          >
            <div className="min-w-56 flex-1">
              <Field
                label="Novo subtipo"
                value={novo}
                maxLength={160}
                onChange={(event) => setNovo(event.target.value)}
              />
            </div>
            <Button type="submit" size="sm" loading={criar.isPending}>
              Cadastrar subtipo
            </Button>
          </form>
        </div>
      ) : null}
    </div>
  );
}

function Subtipo({
  subtipo,
  onMudou,
}: {
  subtipo: EndorsementSubtype;
  onMudou: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [novo, setNovo] = useState('');

  const motivos = useQuery({
    queryKey: ['endorsement-reasons', subtipo.id, 'todos'],
    queryFn: ({ signal }) => endorsementCatalog.reasons(subtipo.id, true, signal),
    enabled: aberto,
  });
  const ativar = useMutation({
    mutationFn: () =>
      endorsementCatalog.updateSubtype(subtipo.id, { active: !subtipo.active }),
    onSuccess: onMudou,
    onError: (error: Error) => toast.error(error.message),
  });
  const criar = useMutation({
    mutationFn: () => endorsementCatalog.createReason(subtipo.id, { name: novo }),
    onSuccess: () => {
      setNovo('');
      void queryClient.invalidateQueries({
        queryKey: ['endorsement-reasons', subtipo.id],
      });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="rounded-md bg-surface-sunken px-3 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-ink-900">
          {subtipo.name}
          {subtipo.active ? '' : ' (desativado)'}
        </p>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="ghost"
            loading={ativar.isPending}
            onClick={() => ativar.mutate()}
          >
            {subtipo.active ? 'Desativar' : 'Ativar'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setAberto((valor) => !valor)}
          >
            {aberto ? 'Fechar motivos' : 'Motivos'}
          </Button>
        </div>
      </div>
      {aberto ? (
        <div className="mt-2 flex flex-col gap-2">
          {(motivos.data ?? []).map((motivo) => (
            <Motivo key={motivo.id} motivo={motivo} />
          ))}
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (novo.trim() !== '') criar.mutate();
            }}
          >
            <div className="min-w-56 flex-1">
              <Field
                label="Novo motivo"
                value={novo}
                maxLength={200}
                onChange={(event) => setNovo(event.target.value)}
              />
            </div>
            <Button type="submit" size="sm" loading={criar.isPending}>
              Cadastrar motivo
            </Button>
          </form>
        </div>
      ) : null}
    </div>
  );
}

function Motivo({ motivo }: { motivo: EndorsementReason }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [linhas, setLinhas] = useState<
    { document_name: string; required: boolean }[] | null
  >(null);

  const documentos = useQuery({
    queryKey: ['endorsement-reason-documents', motivo.id],
    queryFn: ({ signal }) => endorsementCatalog.reasonDocuments(motivo.id, signal),
    enabled: aberto,
  });
  const atuais =
    linhas ??
    (documentos.data ?? []).map((item) => ({
      document_name: item.document_name,
      required: item.required,
    }));

  const ativar = useMutation({
    mutationFn: () =>
      endorsementCatalog.updateReason(motivo.id, { active: !motivo.active }),
    onSuccess: () =>
      void queryClient.invalidateQueries({
        queryKey: ['endorsement-reasons', motivo.subtype_id],
      }),
    onError: (error: Error) => toast.error(error.message),
  });
  const gravar = useMutation({
    mutationFn: () =>
      endorsementCatalog.replaceReasonDocuments(
        motivo.id,
        atuais
          .filter((item) => item.document_name.trim() !== '')
          .map((item, indice) => ({ ...item, sort_order: indice })),
      ),
    onSuccess: () => {
      setLinhas(null);
      toast.success('Documentos do motivo gravados.');
      void queryClient.invalidateQueries({
        queryKey: ['endorsement-reason-documents', motivo.id],
      });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="rounded-md border border-line bg-surface px-3 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-ink-900">
          {motivo.name}
          {motivo.active ? '' : ' (desativado)'}
        </p>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="ghost"
            loading={ativar.isPending}
            onClick={() => ativar.mutate()}
          >
            {motivo.active ? 'Desativar' : 'Ativar'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setAberto((valor) => !valor)}
          >
            {aberto ? 'Fechar documentos' : 'Documentos exigidos'}
          </Button>
        </div>
      </div>
      {aberto ? (
        <div className="mt-2 flex flex-col gap-2">
          {atuais.map((linha, indice) => (
            <div key={indice} className="flex flex-wrap items-end gap-2">
              <div className="min-w-48 flex-1">
                <Field
                  label="Documento exigido"
                  value={linha.document_name}
                  maxLength={200}
                  onChange={(event) =>
                    setLinhas(
                      atuais.map((item, posicao) =>
                        posicao === indice
                          ? { ...item, document_name: event.target.value }
                          : item,
                      ),
                    )
                  }
                />
              </div>
              <label className="flex h-10 items-center gap-2 text-xs text-ink-700">
                <input
                  type="checkbox"
                  checked={linha.required}
                  onChange={(event) =>
                    setLinhas(
                      atuais.map((item, posicao) =>
                        posicao === indice
                          ? { ...item, required: event.target.checked }
                          : item,
                      ),
                    )
                  }
                />
                Obrigatorio
              </label>
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  setLinhas(atuais.filter((_, posicao) => posicao !== indice))
                }
              >
                Tirar
              </Button>
            </div>
          ))}
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() =>
                setLinhas([...atuais, { document_name: '', required: false }])
              }
            >
              Acrescentar documento
            </Button>
            <Button
              size="sm"
              loading={gravar.isPending}
              onClick={() => gravar.mutate()}
            >
              Salvar documentos
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
